import {
  dec,
  type estimateRecipe,
  type FermentableType,
  grownFormLabel,
  type HopUse,
  type RecipeHopForm,
  toStorage,
  type UnitId,
  withGrownForm,
  ZERO,
} from "@bl/domain";
import { Plus, Replace, Trash2 } from "lucide-react";
import { type CSSProperties, useState } from "react";
import { useBrewSystems, useItems } from "../api";
import { BeerGlass } from "../components/BeerGlass";
import { inRange, StyleBar } from "../components/StyleBar";
import { showOnHand, showQuantity } from "../format";
import {
  BatchFields,
  type BatchFigures,
  BrewSystemDialog,
  figuresOf,
  NumberField,
  pickedFrom,
} from "../forms/BatchFields";
import { useLayout } from "../layout/LayoutProvider";
import { HOP_USE_LABELS, showGravity, showShortBy, srmColor } from "../recipeFormat";
import { shortVerdict, useShortFrom } from "../shortFrom";
import { type Figure, type GuideStyle, guideEntry, useStyleGuide } from "../styleGuide";
import {
  type Draft,
  type DraftLine,
  exact,
  GRAMS_PER_OUNCE,
  GRAMS_PER_POUND,
  KIND_LABELS,
  LITERS_PER_GALLON,
  missing,
  num,
  shown,
  shownName,
} from "./recipeDraft";

/**
 * The recipe's batch settings, filled from a brew system where it picks one (Scott, 2026-10-02). The
 * figures stay the recipe's own: changing one here leaves the system as it is, and says what the
 * system has instead, to put back.
 */
export function BatchSection({
  draft,
  set,
}: {
  draft: Draft;
  set: (patch: Partial<Draft>) => void;
}) {
  const { data: systems = [] } = useBrewSystems();
  const [naming, setNaming] = useState(false);
  const system = systems.find((s) => s.id === draft.brewSystemId);
  const figures: BatchFigures = draft;
  const pick = (id: string) => {
    const picked = systems.find((s) => s.id === id);
    set(
      picked ? { ...pickedFrom(picked, figures), brewSystemId: picked.id } : { brewSystemId: null },
    );
  };
  return (
    <section className="builder-card">
      <div className="batch-head">
        <h2>Batch</h2>
        <div className="batch-system">
          {systems.length ? (
            <select
              className="select"
              aria-label="Brew system"
              value={system?.id ?? ""}
              onChange={(e) => pick(e.target.value)}
            >
              <option value="">No brew system</option>
              {systems.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          ) : null}
          <button type="button" className="button small" onClick={() => setNaming(true)}>
            <Plus size={14} /> Save as a brew system
          </button>
        </div>
      </div>
      <BatchFields
        value={figures}
        onChange={set}
        system={system ? { name: system.name, figures: figuresOf(system) } : null}
      />
      {naming ? (
        <BrewSystemDialog
          open
          from={figures}
          onClose={() => setNaming(false)}
          onSaved={(saved) => set({ brewSystemId: saved.id })}
        />
      ) : null}
    </section>
  );
}
/**
 * How far the beer ferments, and its carbonation: the beer's, not the brew system's (Scott's Jam,
 * 2026-10-02: "a place to put either expected attenuation 80% or expected final gravity that shows
 * attenuiation percentage to properly calculate abv"). Typing one of attenuation and FG empties the
 * other, which is then worked out from the OG and shown in gray, and can be clicked to change (Scott's
 * Jam, 2026-10-08: "Attenuation should be editable"). Both empty leaves it to the yeast, adjusted for
 * the mash temperature; a typed figure is not.
 * Its days in a fermenter and a brite tank are here too (Tyler, 2026-10-05): they are the recipe's own
 * and nobody else's (Scott's Jam, 2026-10-08), starting at 14 and 3. The fermenter's are three times,
 * one after another, that add up (Scott's Jam, 2026-10-08: "instead of time in fermenter, we could
 * write fermentation time, dry hop time, and bright tank time", and "logger slash crash time").
 */
export function FermentationSection({
  draft,
  set,
  estimate,
}: {
  draft: Draft;
  set: (patch: Partial<Draft>) => void;
  estimate: ReturnType<typeof estimateRecipe> | null;
}) {
  const byAttenuation = draft.expectedAttenuationPct.trim() !== "";
  const byFg = draft.expectedFg.trim() !== "";
  const attenuation = estimate?.attenuationPct ?? null;
  const fg = estimate?.fg ?? null;
  // The yeast that goes furthest decides, where there are several.
  const yeast = draft.lines
    .filter((l) => l.kind === "yeast" && Number.isFinite(num(l.attenuationPct)))
    .sort((a, b) => num(b.attenuationPct) - num(a.attenuationPct))[0];
  const mash = attenuation === null || !yeast ? 0 : attenuation - num(yeast.attenuationPct);
  // A typed figure is the brewer's own call on the mash (Scott, 2026-10-08: a hotter mash "can reduce
  // attenuation if unfermentable sugars are created"), so the mash temperature does not move it.
  const untilEmptied = (typed: string) =>
    yeast ? ` Empty the ${typed} to go by the yeast and the mash.` : "";
  const note = byAttenuation
    ? fg === null
      ? null
      : `FG ${showGravity(fg)} at the ${draft.expectedAttenuationPct}% you expect, whatever the mash temperature.${untilEmptied("attenuation")}`
    : byFg
      ? attenuation === null
        ? null
        : `${shown(attenuation, 1)}% attenuation to the ${draft.expectedFg} you expect, whatever the mash temperature.${untilEmptied("FG")}`
      : yeast
        ? `From ${yeast.name}'s ${shown(num(yeast.attenuationPct), 1)}%${
            Math.abs(mash) >= 0.05
              ? `, ${shown(Math.abs(mash), 1)}% ${mash > 0 ? "more" : "less"} for a ${draft.mashTempF}°F mash, as BeerSmith works it`
              : ""
          }. Type the attenuation or FG you expect to use that instead.`
        : "Type the attenuation or FG you expect, or add a yeast, for the FG and ABV.";
  return (
    <section className="builder-card">
      <h2>Fermentation</h2>
      <div className="form-grid builder-settings">
        <NumberField
          label="Attenuation"
          unit="%"
          value={draft.expectedAttenuationPct}
          placeholder={byAttenuation || attenuation === null ? undefined : shown(attenuation, 1)}
          onChange={(v) => set({ expectedAttenuationPct: v, expectedFg: "" })}
        />
        <NumberField
          label="FG"
          unit=""
          value={draft.expectedFg}
          placeholder={byFg || fg === null ? undefined : showGravity(fg)}
          onChange={(v) => set({ expectedFg: v, expectedAttenuationPct: "" })}
        />
        <NumberField
          label="Carbonation"
          unit="vols"
          value={draft.carbonationVols}
          onChange={(v) => set({ carbonationVols: v })}
        />
      </div>
      {note ? (
        <p className="muted builder-note" style={{ margin: "8px 0 0" }}>
          {note}
        </p>
      ) : null}
      <div className="form-grid builder-settings" style={{ marginTop: 14 }}>
        <NumberField
          label="Fermentation time"
          unit="days"
          value={draft.fermentationDays}
          onChange={(v) => set({ fermentationDays: v.replace(/\D/g, "") })}
        />
        <NumberField
          label="Dry hop time"
          unit="days"
          value={draft.dryHopDays}
          onChange={(v) => set({ dryHopDays: v.replace(/\D/g, "") })}
        />
        <NumberField
          label="Lager/crash time"
          unit="days"
          value={draft.lagerCrashDays}
          onChange={(v) => set({ lagerCrashDays: v.replace(/\D/g, "") })}
        />
        <NumberField
          label="Brite tank time"
          unit="days"
          value={draft.briteDays}
          onChange={(v) => set({ briteDays: v.replace(/\D/g, "") })}
        />
      </div>
      <p className="faint builder-note" style={{ margin: "8px 0 0" }}>
        {inTanks(draft)} Each brew of it books its tanks for these times, and Production's tank
        schedule draws its bars that long. Type 0 brite tank days for a beer that goes out of the
        fermenter.
      </p>
    </section>
  );
}
/**
 * How long the times typed keep it in each tank: fermentation, dry hop and lager/crash one after another
 * in a fermenter, then the brite tank.
 */
function inTanks(d: Draft): string {
  const typed = [d.fermentationDays, d.dryHopDays, d.lagerCrashDays, d.briteDays].map(num);
  if (!typed.every(Number.isFinite)) return "";
  const [fermentation, dryHop, lagerCrash, brite] = typed as [number, number, number, number];
  const days = (n: number) => `${n} ${n === 1 ? "day" : "days"}`;
  const fermenter = days(fermentation + dryHop + lagerCrash);
  return brite > 0
    ? `${fermenter} in a fermenter, then ${days(brite)} in a brite tank.`
    : `${fermenter} in a fermenter, then out of it with no brite tank.`;
}
export function LineEditor({
  line: l,
  estimate,
  flagged,
  onChange,
  onReplace,
  onRemove,
}: {
  line: DraftLine;
  estimate?: { gristPct?: number; ibu?: number };
  flagged: boolean;
  onChange: (patch: Partial<DraftLine>) => void;
  onReplace: () => void;
  onRemove: () => void;
}) {
  const small = (value: string, field: keyof DraftLine, unit: string, label: string) => (
    <label className="line-field">
      <span className="faint">{label}</span>
      <span className="input-unit">
        <input
          className="input num"
          inputMode="decimal"
          value={value}
          aria-label={`${shownName(l)} ${label}`}
          onChange={(e) => onChange({ [field]: e.target.value.replace(/[^\d.]/g, "") })}
        />
        <span className="unit">{unit}</span>
      </span>
    </label>
  );
  return (
    <div className={`builder-line kind-${l.kind} ${flagged ? "flagged" : ""}`}>
      <div className="line-head">
        <span className="chip plain">{KIND_LABELS[l.kind]}</span>
        {/* Linked, it goes by its item; what the file called it is no longer shown (Scott's Jam,
            2026-10-08). */}
        <strong>{l.itemName ?? l.name}</strong>
        {!l.itemName ? (
          <span className="faint">Not linked</span>
        ) : grownFormLabel(l.itemHopForm) ? (
          <span className="faint">{grownFormLabel(l.itemHopForm)}</span>
        ) : null}
        {/* An imported line is matched to the brewery's own item here (Scott's Jam, 2026-10-08). */}
        <button
          type="button"
          className={l.itemId ? "link-button" : "button small"}
          aria-label={`Replace ${shownName(l)}`}
          onClick={onReplace}
        >
          {l.itemId ? null : <Replace size={14} aria-hidden />}
          Replace
        </button>
        {/* Its share and Remove wrap together, so Remove is never alone on a phone. */}
        <span className="line-end">
          <span className="line-share num">
            {l.kind === "fermentable" && estimate?.gristPct !== undefined
              ? `${estimate.gristPct.toFixed(1)}%`
              : l.kind === "hop" && l.hopUse !== "dry_hop" && estimate?.ibu !== undefined
                ? `${estimate.ibu.toFixed(1)} IBU`
                : ""}
          </span>
          <button
            type="button"
            className="icon-button"
            title={`Remove ${shownName(l)}`}
            onClick={onRemove}
          >
            <Trash2 size={16} />
          </button>
        </span>
      </div>
      <div className="line-fields">
        {l.kind === "fermentable" ? (
          <>
            {small(l.amount, "amount", "lb", "Amount")}
            <label className="line-field">
              <span className="faint">Type</span>
              <select
                className="select"
                value={l.fermentableType}
                onChange={(e) => onChange({ fermentableType: e.target.value as FermentableType })}
              >
                <option value="grain">Grain</option>
                <option value="adjunct">Adjunct</option>
                <option value="sugar">Sugar</option>
                <option value="extract">Extract</option>
                <option value="dry_extract">Dry extract</option>
              </select>
            </label>
            {small(l.yieldPct, "yieldPct", "%", "Yield")}
            {small(l.colorLovibond, "colorLovibond", "°L", "Color")}
          </>
        ) : null}
        {l.kind === "hop" ? (
          <>
            {small(l.amount, "amount", "oz", "Amount")}
            <label className="line-field">
              <span className="faint">Use</span>
              <select
                className="select"
                value={l.hopUse}
                onChange={(e) => onChange({ hopUse: e.target.value as HopUse })}
              >
                {Object.entries(HOP_USE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            {l.hopUse === "dry_hop"
              ? small(l.days, "days", "days", "For")
              : l.hopUse === "boil" || l.hopUse === "whirlpool"
                ? small(l.minutes, "minutes", "min", l.hopUse === "whirlpool" ? "Steep" : "Time")
                : null}
            {l.hopUse === "whirlpool" ? small(l.tempF, "tempF", "°F", "At") : null}
            {small(l.alphaPct, "alphaPct", "%", "Alpha")}
            <label className="line-field">
              <span className="faint">Form</span>
              <select
                className="select"
                value={l.hopForm}
                onChange={(e) => onChange({ hopForm: e.target.value as RecipeHopForm })}
              >
                <option value="pellet">Pellet</option>
                <option value="plug">Plug</option>
                <option value="leaf">Whole cone</option>
              </select>
            </label>
          </>
        ) : null}
        {l.kind === "yeast" ? (
          <>
            {small(l.packages, "packages", "pkg", "Amount")}
            {small(l.attenuationPct, "attenuationPct", "%", "Attenuation")}
          </>
        ) : null}
        {l.kind === "other" ? (
          <>
            <label className="line-field">
              <span className="faint">Amount</span>
              <input
                className="input"
                value={l.amountText}
                onChange={(e) => onChange({ amountText: e.target.value })}
              />
            </label>
            <label className="line-field">
              <span className="faint">When</span>
              <input
                className="input"
                value={l.use}
                onChange={(e) => onChange({ use: e.target.value })}
              />
            </label>
          </>
        ) : null}
      </div>
      {flagged ? <div className="form-error">Needs {missing(l).join(" and ")}.</div> : null}
    </div>
  );
}
export function LivePanel({
  draft,
  estimate,
}: {
  draft: Draft;
  estimate: ReturnType<typeof estimateRecipe> | null;
}) {
  const { data: items = [] } = useItems();
  const { layout } = useLayout();
  const shortFrom = useShortFrom();
  const style = draft.style;
  const { data: guide = [] } = useStyleGuide();
  const entry = guideEntry(guide, style);
  const said = (f: Figure) => entry?.as[f];
  const figures: Figure[] = ["og", "fg", "abv", "ibu", "srm"];
  const anyRange = figures.some((f) => style?.[f]);
  const srm = estimate?.srm ?? 0;
  // What the recipe would take of each linked item, against what is on hand.
  const needs = new Map<string, { name: string; grams: number }>();
  for (const l of draft.lines) {
    if (!l.itemId || (l.kind !== "fermentable" && l.kind !== "hop")) continue;
    const g = num(l.amount) * (l.kind === "hop" ? GRAMS_PER_OUNCE : GRAMS_PER_POUND);
    if (!Number.isFinite(g)) continue;
    const have = needs.get(l.itemId);
    needs.set(l.itemId, {
      name: withGrownForm(l.itemName ?? l.name, l.itemHopForm),
      grams: (have?.grams ?? 0) + g,
    });
  }
  const unlinked = draft.lines.filter((l) => !l.itemId).length;
  return (
    <aside className="builder-panel" style={{ "--beer": srmColor(srm) } as CSSProperties}>
      <div className="panel-figures">
        <BeerGlass style={style?.name} />
        <PanelFigure
          bar={anyRange}
          label="OG"
          places={3}
          value={estimate ? showGravity(estimate.og) : "-"}
          range={style?.og}
          said={said("og")}
          at={estimate?.og}
        />
        <PanelFigure
          bar={anyRange}
          label="FG"
          places={3}
          value={estimate?.fg ? showGravity(estimate.fg) : "-"}
          range={style?.fg}
          said={said("fg")}
          at={estimate?.fg ?? undefined}
        />
        <PanelFigure
          bar={anyRange}
          label="ABV"
          places={1}
          value={estimate?.abv != null ? `${estimate.abv.toFixed(1)}%` : "-"}
          range={style?.abv}
          said={said("abv")}
          at={estimate?.abv ?? undefined}
        />
        <PanelFigure
          bar={anyRange}
          label="IBU"
          value={estimate ? estimate.ibu.toFixed(1) : "-"}
          range={style?.ibu}
          said={said("ibu")}
          at={estimate?.ibu}
        />
        <PanelFigure
          bar={anyRange}
          label="SRM"
          value={estimate ? srm.toFixed(1) : "-"}
          range={style?.srm}
          said={said("srm")}
          at={estimate?.srm}
        />
      </div>
      {/* What the bars and the gray under each figure are, which Scott could not tell (Jam, 2026-10-02). */}
      {anyRange ? (
        <p className="faint panel-note">
          Each bar's green is the style's range, and the mark is this recipe.
        </p>
      ) : figures.some((f) => said(f)) ? (
        <p className="faint panel-note">
          In gray under each figure, what the style guide says of it.
        </p>
      ) : null}
      {estimate ? (
        <p className="faint panel-note">
          Pre-boil {(estimate.preBoilLiters / LITERS_PER_GALLON).toFixed(1)} gal at{" "}
          {showGravity(estimate.preBoilGravity)}. Estimates, as BeerSmith works them.
        </p>
      ) : null}
      {entry ? <StyleNotes entry={entry} /> : null}
      <h3>Inventory</h3>
      {needs.size === 0 ? (
        <p className="faint">Nothing linked to inventory yet.</p>
      ) : (
        <ul className="panel-stock">
          {[...needs].map(([itemId, need]) => {
            const it = items.find((i) => i.id === itemId);
            // Against what is available: what the schedule already plans is not there for this recipe.
            // Short by what that leaves uncovered, as the recipe's page and To order count it, and said
            // as the recipe's page says it: in the item's unit, and from when.
            const wanted = dec(exact(need.grams));
            const enough = it ? dec(it.available).gte(wanted) : false;
            const now = it ? dec(it.onHand.quantity).lt(wanted) : true;
            const planned = it ? dec(it.planned).gt(ZERO) : false;
            const left = it && dec(it.available).gt(ZERO) ? dec(it.available) : ZERO;
            const short = wanted.minus(left);
            const { tone, when } = shortVerdict(
              enough,
              now,
              enough || now ? null : shortFrom(itemId),
            );
            return (
              <li key={itemId}>
                <span>{need.name}</span>
                <span className={`chip ${tone}`}>
                  {enough
                    ? "On hand"
                    : it && short.gt(ZERO)
                      ? `Short ${showShortBy(toStorage(short), it.displayUnit as UnitId)}`
                      : "Short"}
                </span>
                {when ? <span className={`line-stock-when ${tone}`}>{when}</span> : null}
                {it ? (
                  <span className="faint num">
                    {showOnHand(it, layout.inventory.quantityMode)} on hand
                    {planned && it
                      ? `, ${showQuantity(it, it.planned, layout.inventory.quantityMode)} planned`
                      : ""}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {unlinked ? <p className="faint">{unlinked} not linked, so not checked.</p> : null}
      <p className="faint panel-note">
        Writing a recipe uses nothing; a brew does. Checked against what the calendar leaves
        available.
      </p>
    </aside>
  );
}
function PanelFigure({
  label,
  value,
  range,
  at,
  places,
  said,
  bar,
}: {
  label: string;
  value: string;
  range?: { min: number; max: number } | null;
  at?: number;
  /** Decimal places for the range; as given when unset. */
  places?: number;
  /** The guide's own words for a figure it gives no plain range for ("40+", "Varies with style"). */
  said?: string;
  /** A bar under the figure, empty without a range, when any of the style's figures has one. */
  bar: boolean;
}) {
  const out = range && at !== undefined ? !inRange(range, at) : false;
  const shown = (n: number) => (places === undefined ? String(n) : n.toFixed(places));
  return (
    <div
      className="panel-figure"
      title={
        range
          ? `The style's range is ${range.min} to ${range.max}${out ? "; this recipe is outside it" : ""}`
          : undefined
      }
    >
      <span className="faint">{label}</span>
      <strong className={`num ${out ? "out-of-range" : ""}`}>{value}</strong>
      {bar ? <StyleBar range={range} value={at} /> : null}
      {/* The style's range, in gray underneath, to write the recipe to (Scott, 2026-10-02). */}
      {range ? (
        <span className="faint num panel-range">
          {shown(range.min)}-{shown(range.max)}
        </span>
      ) : said ? (
        <span className="faint panel-range">{said}</span>
      ) : null}
    </div>
  );
}
/**
 * What the guide says of the style, beside the recipe as it is written (Scott, 2026-10-02): its color,
 * bitterness and body, and the rest a tap away.
 */
function StyleNotes({ entry }: { entry: GuideStyle }) {
  const d = entry.describe;
  const line = (label: string, text: string | undefined) =>
    text ? (
      <p key={label}>
        <span className="faint">{label}</span> {text}
      </p>
    ) : null;
  return (
    <div className="panel-style">
      <h3>{entry.name}</h3>
      <p className="faint">
        {entry.guide}, {entry.code}. {entry.group}
      </p>
      {line("Color", d.color)}
      {line("Bitterness", d.bitterness)}
      {line("Body", d.body)}
      <details>
        <summary>More about this style</summary>
        {line("Malt", d.malt)}
        {line("Hops", d.hops)}
        {line("Fermentation", d.fermentation)}
        {line("Clarity", d.clarity)}
        {line("Notes", d.notes)}
      </details>
    </div>
  );
}
